import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'

export const Route = createFileRoute('/unsubscribe')({
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: 'Disiscrizione email · LexAIPro' },
      { name: 'robots', content: 'noindex,nofollow' },
    ],
  }),
})

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; email: string }
  | { kind: 'used' }
  | { kind: 'invalid' }
  | { kind: 'submitting' }
  | { kind: 'done' }
  | { kind: 'error'; message: string }

function UnsubscribePage() {
  const [state, setState] = useState<State>({ kind: 'loading' })

  const token = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('token') ?? ''
    : ''

  useEffect(() => {
    if (!token) { setState({ kind: 'invalid' }); return }
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
        const data = await res.json().catch(() => ({}))
        if (cancelled) return
        if (!res.ok) { setState({ kind: 'invalid' }); return }
        if (data.used) setState({ kind: 'used' })
        else setState({ kind: 'ready', email: data.email ?? '' })
      } catch {
        if (!cancelled) setState({ kind: 'error', message: 'Errore di rete' })
      }
    })()
    return () => { cancelled = true }
  }, [token])

  const confirm = async () => {
    setState({ kind: 'submitting' })
    try {
      const res = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      if (!res.ok) {
        const t = await res.text().catch(() => '')
        setState({ kind: 'error', message: t || 'Errore' })
        return
      }
      setState({ kind: 'done' })
    } catch {
      setState({ kind: 'error', message: 'Errore di rete' })
    }
  }

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm text-center">
        <h1 className="font-display text-2xl font-bold text-foreground">
          Disiscrizione email
        </h1>

        {state.kind === 'loading' && (
          <div className="mt-6 flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p>Verifica del link...</p>
          </div>
        )}

        {state.kind === 'invalid' && (
          <div className="mt-6 space-y-3">
            <XCircle className="mx-auto h-10 w-10 text-destructive" />
            <p className="text-sm text-muted-foreground">
              Il link non è valido o è scaduto.
            </p>
          </div>
        )}

        {state.kind === 'used' && (
          <div className="mt-6 space-y-3">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <p className="text-sm text-muted-foreground">
              Sei già stato rimosso dalle nostre comunicazioni.
            </p>
          </div>
        )}

        {state.kind === 'ready' && (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-muted-foreground">
              Confermi di voler smettere di ricevere email da LexAIPro
              {state.email ? ` all'indirizzo ${state.email}` : ''}?
            </p>
            <Button onClick={confirm} className="w-full">
              Conferma disiscrizione
            </Button>
          </div>
        )}

        {state.kind === 'submitting' && (
          <div className="mt-6 flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p>Disiscrizione in corso...</p>
          </div>
        )}

        {state.kind === 'done' && (
          <div className="mt-6 space-y-3">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <p className="text-sm text-muted-foreground">
              Disiscrizione completata. Non riceverai più email da LexAIPro.
            </p>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="mt-6 space-y-3">
            <XCircle className="mx-auto h-10 w-10 text-destructive" />
            <p className="text-sm text-muted-foreground">{state.message}</p>
          </div>
        )}
      </div>
    </main>
  )
}
